var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Vue } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import { GoalPlannerTemplate } from '@/models/GoalPlannerTemplate';
import MiCalendar from '@/components/mi-calendar/MiCalendar';
import { defaultCalendarSettings, } from '@/support/CalendarDefaults';
import { GoalPlannerTimeslot } from '@/models/GoalPlannerTimeslot';
import CreateOrEditGoalPlannerTimeslotDialog from '@/views/GoalPlanner/CreateOrEditGoalPlannerTimeslotDialog/CreateOrEditGoalPlannerTimeslotDialog.vue';
import PageHeader from '@/components/page-header/PageHeader';
import LabelValue from '@/components/LabelValue.vue';
let Template = class Template extends Vue {
    constructor() {
        super(...arguments);
        this.$pageTitle = 'Template';
        // #region @VModel
        // #endregion
        // #region @PropSyncs
        // #endregion
        // #region @Props
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.DAYS_SUN_FIRST = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ];
        this.templateId = this.$route.params.id;
        this.template = null;
        this.timeslots = [];
        this.hasWeekends = false;
        this.isCreatingOrEditingTimeslot = false;
        this.newTimeslot = null;
        this.existingTimeslot = null;
        this.fullBusinessHours = {
            daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
            startTime: '08:00',
            endTime: '18:00',
        };
        this.isLoading = true;
        this.isEditingTemplate = false;
        // #endregion
        // #region @Watchers
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks
    mounted() {
        this.initialize();
    }
    async initialize() {
        this.isLoading = true;
        await Promise.all([this.fetchTemplate(), this.fetchTimeslots()]);
        this.isLoading = false;
    }
    // #endregion
    // #region Event handlers
    onTimeslotCreated() {
        this.isCreatingOrEditingTimeslot = false;
        this.fetchTimeslots();
        this.resetDialogValues();
    }
    onTimeslotUpdated() {
        this.isCreatingOrEditingTimeslot = false;
        this.fetchTimeslots();
        this.resetDialogValues();
    }
    onTimeslotDeleted() {
        this.isCreatingOrEditingTimeslot = false;
        this.fetchTimeslots();
        this.resetDialogValues();
    }
    onCloseCreateOrEditTimeslotDialog() {
        this.isCreatingOrEditingTimeslot = false;
        this.resetDialogValues();
    }
    // #endregion
    // #region Class methods
    resetDialogValues() {
        this.newTimeslot = null;
        this.existingTimeslot = null;
    }
    getFormattedStartTime(startsAt) {
        return startsAt.split('T')[1].split('+')[0].substring(0, 5);
    }
    getFormattedEndTime(endsAt) {
        return endsAt.split('T')[1].split('+')[0].substring(0, 5);
    }
    formatDateToHHMM(date) {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    }
    formatDateTimeStringToHHMM(timeString) {
        if (timeString.includes('T')) {
            timeString = timeString.split('T')[1];
        }
        const [hours, minutes] = timeString.split(':');
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    getDayFromDateTimeString(date) {
        const dayIndex = date.getDay(); // 0 (Sun) to 6 (Sat)
        return this.DAYS_SUN_FIRST[dayIndex];
    }
    formatCalendarSelectionToTimeslot(selectionInfo) {
        const timeslot = {
            day: this.getDayFromDateTimeString(selectionInfo.start),
            starts_at: this.formatDateTimeStringToHHMM(selectionInfo.startStr),
            ends_at: this.formatDateTimeStringToHHMM(selectionInfo.endStr),
        };
        return timeslot;
    }
    formatCalendarEventToTimeslot(selectionInfo) {
        const timeslot = {
            id: selectionInfo.event.id,
            title: selectionInfo.event.title,
            day: this.getDayFromDateTimeString(selectionInfo.event.start),
            starts_at: this.formatDateToHHMM(selectionInfo.event.start),
            ends_at: this.formatDateToHHMM(selectionInfo.event.end),
            color: selectionInfo.event.extendedProps.customColor || '#F9A825',
        };
        return timeslot;
    }
    // #endregion
    // #region Async methods (api calls only)
    async onUpdateTemplate() {
        if (!this.template)
            return;
        try {
            await new GoalPlannerTemplate().update(this.template);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async fetchTemplate() {
        try {
            this.template = await new GoalPlannerTemplate().find(this.templateId);
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async fetchTimeslots() {
        this.timeslots = [];
        try {
            this.timeslots = await new GoalPlannerTimeslot()
                .filter({ template_id: this.templateId })
                .limit(999)
                .all();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    async updateTimeslot(timeslot) {
        const updatedTimeslot = new GoalPlannerTimeslot(timeslot);
        try {
            await updatedTimeslot.update();
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    // #region Getters & Setters
    get calendarEvents() {
        if (!this.template) {
            return [];
        }
        return this.template.type === 'week'
            ? this.formattedTimeslotsForWeekTemplate
            : this.formattedTimeslotsForDayTemplate;
    }
    get formattedTimeslotsForWeekTemplate() {
        if (!this.timeslots)
            return [];
        return this.timeslots.map((timeslot) => ({
            id: timeslot.id,
            title: timeslot.title,
            day: timeslot.day,
            daysOfWeek: [this.DAYS_SUN_FIRST.indexOf(timeslot.day)],
            startTime: timeslot.starts_at,
            endTime: timeslot.ends_at,
            customColor: timeslot.color,
        }));
    }
    get formattedTimeslotsForDayTemplate() {
        if (!this.timeslots)
            return [];
        return this.timeslots.map((timeslot) => ({
            id: timeslot.id,
            title: timeslot.title,
            startTime: timeslot.starts_at,
            endTime: timeslot.ends_at,
            customColor: timeslot.color,
        }));
    }
    get calendarOptions() {
        const options = {
            ...defaultCalendarSettings,
            editable: this.canEdit,
            selectable: this.canEdit,
            buttonText: {
                today: this.$t('rosterManagement.today'),
            },
            header: {
                left: '',
                right: '',
            },
            defaultView: this.currentView,
            firstDay: this.weekStart,
            weekends: this.hasWeekends,
            eventOverlap: false,
            selectOverlap: false,
            columnHeaderFormat: {
                weekday: 'long',
            },
            columnHeader: this.isDayView ? false : true,
            businessHours: this.fullBusinessHours,
            slotDuration: '00:05:00',
            eventConstraint: 'businessHours',
            selectConstraint: 'businessHours',
            ...(this.canEdit && {
                eventClick: (selection) => {
                    this.existingTimeslot = this.formatCalendarEventToTimeslot(selection);
                    this.isCreatingOrEditingTimeslot = true;
                },
            }),
            select: (selectionInfo) => {
                this.newTimeslot = this.formatCalendarSelectionToTimeslot(selectionInfo);
                this.isCreatingOrEditingTimeslot = true;
            },
            eventDrop: (eventDropInfo) => {
                const timeslot = this.formatCalendarEventToTimeslot(eventDropInfo);
                this.updateTimeslot(timeslot);
            },
            eventResize: (eventResizeInfo) => {
                const timeslot = this.formatCalendarEventToTimeslot(eventResizeInfo);
                this.updateTimeslot(timeslot);
            },
            eventRender: (info) => {
                const backgroundColor = info.event.extendedProps.customColor || '#F9A825';
                info.el.style.backgroundColor = backgroundColor;
                if (backgroundColor === '#FFEE58' || backgroundColor === '#64FFDA') {
                    info.el.style.color = 'black';
                }
                else {
                    info.el.style.color = 'white';
                }
            },
        };
        return options;
    }
    get currentView() {
        if (!this.template)
            return 'timeGridWeek';
        return this.template.type === 'week' ? 'timeGridWeek' : 'timeGridDay';
    }
    get isDayView() {
        return this.currentView === 'timeGridDay';
    }
    get templateType() {
        if (!this.template || !this.template.type)
            return '';
        return this.template.type === 'week' ? this.$tc('Week') : this.$tc('Day');
    }
    get weekStart() {
        return 1;
    }
    get canEdit() {
        if (!this.template)
            return false;
        return this.user.id === this.template.user_id;
    }
    get user() {
        return this.$store.state.Auth;
    }
    get pageTitle() {
        if (!this.template || !this.template.title) {
            return this.$tc('Period planner template');
        }
        return this.template.title;
    }
    get breadcrumbs() {
        const secondCrumb = this.template && this.template.title ? this.template.title : '-';
        return [
            { name: this.$tc('Period planner templates'), path: '/goal-planner/templates' },
            { name: secondCrumb },
        ];
    }
};
Template = __decorate([
    Component({
        components: {
            MiCalendar,
            CreateOrEditGoalPlannerTimeslotDialog,
            PageHeader,
            LabelValue,
        },
    })
], Template);
export default Template;
// #region Enums
// #endregion
// #region Interfaces
// #endregion
// #region Constants
// #endregion
